/* IdChip — Iter211 (extended Iter212)
 *
 * Single-source-of-truth chip for Client / Property / Appraisal / Lead
 * IDs across the CRM. Reads colour + font + effect tokens from
 * IdChipThemeContext so the office can rebrand the chips without a
 * code change via /admin/id-chip-theme.
 *
 * Usage:
 *   <IdChip type="client" value="2536" />          → C2536
 *   <IdChip type="property" value="3110" />        → P3110
 *   <IdChip type="appraisal" value="5004" />       → A5004
 *   <IdChip type="lead" value="42" />              → L42
 *
 * Sizes: "sm" | "md" (default) | "lg" — these scale the *base* font
 * size from the theme by 0.85× / 1× / 1.15× so admin-set fonts still
 * win the look while the layout stays consistent.
 */
import React from 'react';
import { useIdChipTheme } from '../contexts/IdChipThemeContext';

const PREFIX = { client: 'C', property: 'P', appraisal: 'A', lead: 'L' };
const TITLE  = { client: 'Client ID', property: 'Property ID', appraisal: 'Appraisal ID', lead: 'Lead ID' };
const SIZE_SCALE = { sm: 0.85, md: 1, lg: 1.15 };

function scaleRemString(value, factor) {
  if (!value || factor === 1) return value;
  const m = String(value).match(/^([\d.]+)(rem|em|px)?$/);
  if (!m) return value;
  const n = parseFloat(m[1]) * factor;
  return `${n.toFixed(4).replace(/\.?0+$/, '')}${m[2] || 'rem'}`;
}

export default function IdChip({ type, value, prefix, size = 'md', className = '', testId, style: styleOverride }) {
  const { theme } = useIdChipTheme();
  if (value === null || value === undefined || value === '') return null;
  const pal = theme[type] || theme.client;
  const factor = SIZE_SCALE[size] ?? 1;

  const style = {
    backgroundColor: pal.bg,
    borderColor:     pal.border,
    color:           pal.text,
    fontFamily:      pal.fontFamily,
    fontWeight:      pal.fontWeight,
    fontStyle:       pal.fontStyle,
    fontSize:        scaleRemString(pal.fontSize, factor),
    letterSpacing:   pal.letterSpacing,
    textTransform:   pal.textTransform,
    borderRadius:    pal.borderRadius,
    borderWidth:     pal.borderWidth,
    borderStyle:     pal.borderStyle,
    boxShadow:       pal.boxShadow,
    textShadow:      pal.textShadow,
    paddingLeft:     pal.paddingX,
    paddingRight:    pal.paddingX,
    paddingTop:      pal.paddingY,
    paddingBottom:   pal.paddingY,
    display:         'inline-flex',
    alignItems:      'center',
    lineHeight:      1.15,
    ...styleOverride,
  };

  return (
    <span
      data-testid={testId || `id-chip-${type}`}
      title={TITLE[type] || ''}
      className={`id-chip ${className}`}
      style={style}
    >
      {prefix ?? PREFIX[type] ?? ''}{value}
    </span>
  );
}
